import React, { useState, useCallback } from 'react';
import { Upload, Download, Settings, Maximize2, FileImage, Loader2, AlertCircle, CheckCircle, Lock, Unlock } from 'lucide-react';
import { API_CONFIG, getApiUrl, validateFileSize, validateFileType, formatFileSize } from '../config/api';

interface ResizeSettings {
  width: number | null;
  height: number | null;
  maintainAspect: boolean;
  resizeMode: string;
  outputFormat: string;
  quality: number;
  backgroundColor: string;
}

interface ResizePreset {
  name: string;
  description: string;
  width: number;
  height: number;
  resize_mode: string;
}

interface ResizeResult {
  file: File;
  originalSize: number;
  resizedSize: number;
  originalDimensions: string;
  newDimensions: string;
  downloadUrl: string;
}

const ImageResizer: React.FC = () => {
  const [files, setFiles] = useState<File[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [results, setResults] = useState<ResizeResult[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [presets, setPresets] = useState<Record<string, ResizePreset>>({});
  const [selectedPreset, setSelectedPreset] = useState<string>('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [previewDimensions, setPreviewDimensions] = useState<{width: number, height: number} | null>(null);
  
  const [settings, setSettings] = useState<ResizeSettings>({
    width: 1920,
    height: 1080,
    maintainAspect: true,
    resizeMode: 'fit',
    outputFormat: 'original',
    quality: 95,
    backgroundColor: '#FFFFFF',
  });

  // Load presets on component mount
  React.useEffect(() => {
    loadPresets();
  }, []);

  const loadPresets = async () => {
    try {
      const response = await fetch(getApiUrl('/imagetool/presets/resize'));
      if (response.ok) {
        const data = await response.json();
        setPresets(data.presets);
      }
    } catch (error) {
      console.error('Failed to load presets:', error);
    }
  };

  const applyPreset = (presetKey: string) => {
    const preset = presets[presetKey];
    if (preset) {
      setSettings(prev => ({
        ...prev,
        width: preset.width,
        height: preset.height,
        resizeMode: preset.resize_mode,
      }));
      setSelectedPreset(presetKey);
    }
  };

  const handleFileSelect = useCallback((selectedFiles: FileList | null) => {
    if (!selectedFiles) return;

    const validFiles: File[] = [];
    const errors: string[] = [];

    Array.from(selectedFiles).forEach((file) => {
      if (!validateFileType(file)) {
        errors.push(`${file.name}: Unsupported file type`);
        return;
      }
      if (!validateFileSize(file)) {
        errors.push(`${file.name}: File too large (max ${formatFileSize(API_CONFIG.MAX_FILE_SIZE)})`);
        return;
      }
      validFiles.push(file);
    });

    if (errors.length > 0) {
      setError(errors.join(', '));
    } else {
      setError(null);
    }

    setFiles(validFiles);
    setResults([]);
    
    // Get preview dimensions from first file
    if (validFiles.length > 0) {
      const img = new window.Image();
      img.onload = () => {
        setPreviewDimensions({ width: img.width, height: img.height });
      };
      img.src = URL.createObjectURL(validFiles[0]);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    handleFileSelect(e.dataTransfer.files);
  }, [handleFileSelect]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const resizeImages = async () => {
    if (files.length === 0) return;
    if (!settings.width && !settings.height) {
      setError('Please specify at least width or height');
      return;
    }

    setIsProcessing(true);
    setError(null);
    setResults([]);

    try {
      if (files.length === 1) {
        const result = await resizeSingleImage(files[0]);
        setResults([result]);
      } else {
        await resizeBatchImages();
      }
    } catch (error) {
      console.error('Resize error:', error);
      setError(error instanceof Error ? error.message : 'Resize failed');
    } finally {
      setIsProcessing(false);
    }
  };

  const resizeSingleImage = async (file: File): Promise<ResizeResult> => {
    const formData = new FormData();
    formData.append('file', file);
    if (settings.width) formData.append('width', settings.width.toString());
    if (settings.height) formData.append('height', settings.height.toString());
    formData.append('maintain_aspect', settings.maintainAspect.toString());
    formData.append('resize_mode', settings.resizeMode);
    formData.append('output_format', settings.outputFormat);
    formData.append('quality', settings.quality.toString());
    formData.append('background_color', settings.backgroundColor);

    const response = await fetch(getApiUrl('/imagetool/resize'), {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || 'Resize failed');
    }

    const originalSizeHeader = response.headers.get('X-Original-Size') || response.headers.get('x-original-size');
    const resizedSizeHeader = response.headers.get('X-Resized-Size') || response.headers.get('x-resized-size');
    const originalDimensionsHeader = response.headers.get('X-Original-Dimensions') || response.headers.get('x-original-dimensions');
    const newDimensionsHeader = response.headers.get('X-New-Dimensions') || response.headers.get('x-new-dimensions');

    const blob = await response.blob();
    const downloadUrl = URL.createObjectURL(blob);

    return {
      file: new File([blob], file.name, { type: blob.type }),
      originalSize: parseInt(originalSizeHeader || '0') || file.size,
      resizedSize: parseInt(resizedSizeHeader || '0') || blob.size,
      originalDimensions: originalDimensionsHeader || '',
      newDimensions: newDimensionsHeader || '',
      downloadUrl,
    };
  };

  const resizeBatchImages = async () => {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    if (settings.width) formData.append('width', settings.width.toString());
    if (settings.height) formData.append('height', settings.height.toString());
    formData.append('maintain_aspect', settings.maintainAspect.toString());
    formData.append('resize_mode', settings.resizeMode);
    formData.append('output_format', settings.outputFormat);
    formData.append('quality', settings.quality.toString());

    const response = await fetch(getApiUrl('/imagetool/resize/batch'), {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || 'Batch resize failed');
    }

    const totalFiles = response.headers.get('X-Total-Files') || '0';
    const originalSizeHeader = response.headers.get('X-Original-Size') || '0';
    const resizedSizeHeader = response.headers.get('X-Resized-Size') || '0';

    const blob = await response.blob();
    const downloadUrl = URL.createObjectURL(blob);
    
    const zipResult: ResizeResult = {
      file: new File([blob], 'resized_images.zip', { type: 'application/zip' }),
      originalSize: parseInt(originalSizeHeader) || files.reduce((sum, file) => sum + file.size, 0),
      resizedSize: parseInt(resizedSizeHeader) || blob.size,
      originalDimensions: `${totalFiles} files`,
      newDimensions: `${settings.width || 'auto'}x${settings.height || 'auto'}`,
      downloadUrl,
    };

    setResults([zipResult]);
  };

  const downloadResult = (result: ResizeResult) => {
    const link = document.createElement('a');
    link.href = result.downloadUrl;
    link.download = result.file.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const clearFiles = () => {
    setFiles([]);
    setResults([]);
    setError(null);
    setPreviewDimensions(null);
  };

  const resizeModes = [
    { id: 'fit', name: 'Fit', description: 'Fit within dimensions (letterbox if needed)' },
    { id: 'crop', name: 'Crop', description: 'Fill and crop from center' },
    { id: 'stretch', name: 'Stretch', description: 'Stretch to exact dimensions' },
  ];

  return (
    <div className="space-y-6">
      <div className="text-center">
        <div className="flex justify-center mb-4">
          <div className="p-3 bg-purple-100 rounded-full">
            <Maximize2 className="w-8 h-8 text-purple-600" />
          </div>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Image Resizer</h2>
        <p className="text-gray-600">
          Resize images to exact dimensions. Perfect for social media, web, and print.
        </p>
      </div>

      {/* Preset Selection */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h3 className="text-lg font-semibold mb-3">Quick Presets</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
          {Object.entries(presets).map(([key, preset]) => (
            <button
              key={key}
              onClick={() => applyPreset(key)}
              className={`p-2 rounded-lg border text-left transition-all text-sm ${
                selectedPreset === key
                  ? 'border-purple-500 bg-purple-50 text-purple-900'
                  : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <div className="font-medium truncate">{preset.name}</div>
              <div className="text-xs text-gray-500">{preset.width}×{preset.height}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Dimension Settings */}
      <div className="bg-white border rounded-lg p-4">
        <h3 className="text-lg font-semibold mb-4">Dimensions</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Width (px)
            </label>
            <input
              type="number"
              value={settings.width || ''}
              onChange={(e) => {
                setSettings(prev => ({ ...prev, width: e.target.value ? parseInt(e.target.value) : null }));
                setSelectedPreset('');
              }}
              placeholder="Auto"
              min="1"
              max="10000"
              className="w-full p-2 border border-gray-300 rounded-md"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Height (px)
            </label>
            <input
              type="number"
              value={settings.height || ''}
              onChange={(e) => {
                setSettings(prev => ({ ...prev, height: e.target.value ? parseInt(e.target.value) : null }));
                setSelectedPreset('');
              }}
              placeholder="Auto"
              min="1"
              max="10000"
              className="w-full p-2 border border-gray-300 rounded-md"
            />
          </div>
        </div>

        {previewDimensions && (
          <div className="mt-3 text-sm text-gray-600">
            Original: {previewDimensions.width}×{previewDimensions.height}px
          </div>
        )}

        {/* Aspect Ratio Lock */}
        <div className="mt-4">
          <button
            onClick={() => setSettings(prev => ({ ...prev, maintainAspect: !prev.maintainAspect }))}
            className={`flex items-center px-3 py-2 rounded-lg border transition-all ${
              settings.maintainAspect
                ? 'border-purple-500 bg-purple-50 text-purple-700'
                : 'border-gray-300 bg-gray-50 text-gray-600'
            }`}
          >
            {settings.maintainAspect ? (
              <Lock className="w-4 h-4 mr-2" />
            ) : (
              <Unlock className="w-4 h-4 mr-2" />
            )}
            {settings.maintainAspect ? 'Aspect Ratio Locked' : 'Aspect Ratio Unlocked'}
          </button>
        </div>

        {/* Resize Mode */}
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Resize Mode
          </label>
          <div className="grid grid-cols-3 gap-2">
            {resizeModes.map((mode) => (
              <button
                key={mode.id}
                onClick={() => setSettings(prev => ({ ...prev, resizeMode: mode.id }))}
                className={`p-3 rounded-lg border text-center transition-all ${
                  settings.resizeMode === mode.id
                    ? 'border-purple-500 bg-purple-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="font-medium text-sm">{mode.name}</div>
                <div className="text-xs text-gray-500 mt-1">{mode.description}</div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Advanced Settings */}
      <div className="bg-white border rounded-lg">
        <button
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="w-full p-4 flex items-center justify-between text-left hover:bg-gray-50"
        >
          <div className="flex items-center">
            <Settings className="w-5 h-5 text-gray-500 mr-2" />
            <span className="font-medium">Advanced Settings</span>
          </div>
          <span className="text-gray-400">{showAdvanced ? '−' : '+'}</span>
        </button>
        
        {showAdvanced && (
          <div className="border-t p-4 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Output Format
                </label>
                <select
                  value={settings.outputFormat}
                  onChange={(e) => setSettings(prev => ({ ...prev, outputFormat: e.target.value }))}
                  className="w-full p-2 border border-gray-300 rounded-md"
                >
                  <option value="original">Keep Original</option>
                  <option value="jpeg">JPEG</option>
                  <option value="png">PNG</option>
                  <option value="webp">WebP</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Quality: {settings.quality}%
                </label>
                <input
                  type="range"
                  min="1"
                  max="100"
                  value={settings.quality}
                  onChange={(e) => setSettings(prev => ({ ...prev, quality: parseInt(e.target.value) }))}
                  className="w-full"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Background Color (for letterboxing)
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="color"
                    value={settings.backgroundColor}
                    onChange={(e) => setSettings(prev => ({ ...prev, backgroundColor: e.target.value }))}
                    className="w-10 h-10 rounded border border-gray-300 cursor-pointer"
                  />
                  <input
                    type="text"
                    value={settings.backgroundColor}
                    onChange={(e) => setSettings(prev => ({ ...prev, backgroundColor: e.target.value }))}
                    className="flex-1 p-2 border border-gray-300 rounded-md"
                    placeholder="#FFFFFF"
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* File Upload */}
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-gray-400 transition-colors"
      >
        <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Drop images here or click to select
        </h3>
        <p className="text-gray-600 mb-4">
          Supports PNG, JPG, WebP, BMP, TIFF up to {formatFileSize(API_CONFIG.MAX_FILE_SIZE)}
        </p>
        <input
          type="file"
          multiple
          accept={API_CONFIG.SUPPORTED_FORMATS.INPUT.join(',')}
          onChange={(e) => handleFileSelect(e.target.files)}
          className="hidden"
          id="resize-file-upload"
        />
        <label
          htmlFor="resize-file-upload"
          className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 cursor-pointer"
        >
          <FileImage className="w-4 h-4 mr-2" />
          Select Images
        </label>
      </div>

      {/* Selected Files */}
      {files.length > 0 && (
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex justify-between items-center mb-3">
            <h3 className="font-medium">Selected Files ({files.length})</h3>
            <button
              onClick={clearFiles}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Clear All
            </button>
          </div>
          <div className="space-y-2">
            {files.map((file, index) => (
              <div key={index} className="flex items-center justify-between p-2 bg-white rounded border">
                <div className="flex items-center">
                  <FileImage className="w-4 h-4 text-gray-400 mr-2" />
                  <span className="text-sm">{file.name}</span>
                </div>
                <span className="text-xs text-gray-500">{formatFileSize(file.size)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
          <AlertCircle className="w-5 h-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
          <div className="text-red-700">{error}</div>
        </div>
      )}

      {/* Resize Button */}
      {files.length > 0 && (
        <button
          onClick={resizeImages}
          disabled={isProcessing || (!settings.width && !settings.height)}
          className="w-full bg-purple-600 text-white py-3 px-4 rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
        >
          {isProcessing ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Resizing...
            </>
          ) : (
            <>
              <Maximize2 className="w-4 h-4 mr-2" />
              Resize {files.length} Image{files.length > 1 ? 's' : ''}
            </>
          )}
        </button>
      )}

      {/* Results */}
      {results.length > 0 && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center mb-3">
            <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
            <h3 className="font-medium text-green-900">Resize Complete!</h3>
          </div>
          
          {results.map((result, index) => (
            <div key={index} className="bg-white rounded-lg p-4 mb-3 last:mb-0">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium">{result.file.name}</span>
                <button
                  onClick={() => downloadResult(result)}
                  className="flex items-center px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700"
                >
                  <Download className="w-4 h-4 mr-1" />
                  Download
                </button>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Original Size:</span>
                  <div className="font-medium">{formatFileSize(result.originalSize)}</div>
                </div>
                <div>
                  <span className="text-gray-600">New Size:</span>
                  <div className="font-medium">{formatFileSize(result.resizedSize)}</div>
                </div>
                <div>
                  <span className="text-gray-600">Original:</span>
                  <div className="font-medium">{result.originalDimensions}</div>
                </div>
                <div>
                  <span className="text-gray-600">New:</span>
                  <div className="font-medium">{result.newDimensions}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ImageResizer;
